import { useCallback, useEffect, useMemo, useState } from "react";

import { getPersistedItem, setPersistedItem } from "@common/util/localStorage";
import { useAuthContext } from "@main/context/Auth/Auth.context";

/**
 * Remembers which interjected rows the user has dismissed.
 *
 * Dismissal is permanently client-side (the backend will never own placement/dismissal for these
 * rows), scoped per user rather than per workspace - dismissing hides a row for that person only,
 * on any device, forever, until the row itself changes id (e.g. a later milestone re-triggers it).
 */

export function useInterjectedRowDismissal(rowIds: string[]) {
  const { userData } = useAuthContext();
  const userId = userData?.id;

  const [dismissedIds, setDismissedIds] = useState<string[]>([]);

  const rowIdsKey = useMemo(() => rowIds.join(","), [rowIds]);

  useEffect(() => {
    if (!userId) {
      setDismissedIds([]);
      return;
    }
    setDismissedIds(readDismissedIds(userId, rowIdsKey ? rowIdsKey.split(",") : []));
  }, [userId, rowIdsKey]);

  const dismiss = useCallback(
    (rowId: string) => {
      setDismissedIds((previous) => (previous.includes(rowId) ? previous : [...previous, rowId]));

      if (!userId) return;
      persistDismissal(userId, rowId);
    },
    [userId],
  );

  return { dismissedIds, dismiss };
}

const KEY_PREFIX = "shakebugs.interjectedRow";

const storageKey = (userId: string, rowId: string) => `${KEY_PREFIX}.${userId}.${rowId}`;

function readDismissedIds(userId: string, rowIds: string[]) {
  return rowIds.filter((rowId) => Boolean(getPersistedItem(storageKey(userId, rowId))));
}

function persistDismissal(userId: string, rowId: string) {
  setPersistedItem(storageKey(userId, rowId), true);
}
